import { getOfflineStatusCopy } from '@affine/core/components/offline-status';
import {
  FolderApiService,
  type GroupData,
} from '@affine/core/modules/folder-api';
import type { Member } from '@affine/core/modules/permissions';
import type { PermissionRoleMenuOption } from '@affine/core/modules/permissions/components/permission-role-menu';
import { PermissionRoleMenu } from '@affine/core/modules/permissions/components/permission-role-menu';
import { createObjectPermissionRoleOptions } from '@affine/core/modules/permissions/utils/object-permission-ui';
import {
  PermissionGroupInviteSearch,
  PermissionGroupRow,
  PermissionMemberInviteSearch,
  PermissionMemberRow,
} from '@affine/core/modules/permissions/views/permission-invite-picker';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from './permission-create-modal-core.css';

export type PermissionInviteMode = 'create' | 'share';
export type PermissionInviteTarget = 'people' | 'groups';

export interface PendingPermissionInvite {
  userId: string;
  userName: string;
  userEmail: string;
  userAvatarUrl?: string;
  role: string;
}

export interface PendingPermissionGroupInvite {
  groupId: string;
  groupName: string;
  role: string;
}

export interface PermissionInviteCopy {
  title: string;
  placeholder: string;
  noResultsText: string;
  offlineMessage: string;
}

export const matchesPermissionQuery = (
  query: string,
  values: readonly (string | undefined)[]
) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }
  return values.some(value => value?.toLowerCase().includes(normalizedQuery));
};

export function usePermissionInviteCopy(
  mode: PermissionInviteMode,
  target: PermissionInviteTarget
) {
  const t = useI18n();

  return useMemo((): PermissionInviteCopy => {
    const offlineMessage = getOfflineStatusCopy(
      t,
      target === 'groups' ? 'groups-online-only' : 'people-online-only'
    );

    if (mode === 'create' && target === 'people') {
      return {
        title:
          t['com.streamlain.settings.workspace.folders.modal.add-people']?.() ??
          'Add people',
        placeholder:
          t[
            'com.streamlain.settings.workspace.folders.modal.search-placeholder'
          ]?.() ??
          t['com.streamlain.share-menu.invite-editor.placeholder']?.() ??
          'Search workspace members...',
        noResultsText:
          t[
            'com.streamlain.settings.workspace.folders.modal.no-members-found'
          ]?.() ?? 'No members found',
        offlineMessage,
      };
    }

    if (mode === 'create' && target === 'groups') {
      return {
        title:
          t['com.streamlain.settings.workspace.folders.modal.groups-add']?.() ??
          'Add group',
        placeholder:
          t[
            'com.streamlain.settings.workspace.permissions.search-groups'
          ]?.() ??
          t['com.streamlain.settings.workspace.folders.search-groups']?.() ??
          'Search groups...',
        noResultsText:
          t[
            'com.streamlain.settings.workspace.permissions.no-groups-found'
          ]?.() ?? 'No groups found',
        offlineMessage,
      };
    }

    if (target === 'people') {
      return {
        title: t['com.streamlain.folder-share.add-people']?.() ?? 'Add people',
        placeholder:
          t['com.streamlain.folder-share.search-placeholder']?.() ??
          'Search workspace members...',
        noResultsText:
          t['com.streamlain.folder-share.no-members-found']?.() ??
          'No members found',
        offlineMessage,
      };
    }

    return {
      title: t['com.streamlain.folder-share.add-group']?.() ?? 'Add group',
      placeholder:
        t['com.streamlain.folder-share.search-groups-placeholder']?.() ??
        'Search groups...',
      noResultsText:
        t['com.streamlain.folder-share.no-groups-found']?.() ??
        'No groups found',
      offlineMessage,
    };
  }, [mode, target, t]);
}

export function createPermissionRoleOptions<Role extends string>(
  t: ReturnType<typeof useI18n>,
  roles: readonly Role[]
): PermissionRoleMenuOption<Role>[] {
  return createObjectPermissionRoleOptions(t, roles);
}

export function usePermissionRoleOptions<Role extends string>(
  roles: readonly Role[]
): PermissionRoleMenuOption<Role>[] {
  const t = useI18n();
  return useMemo(() => createPermissionRoleOptions(t, roles), [roles, t]);
}

export interface PermissionExpandableSectionProps {
  title: string;
  subtitle?: string;
  defaultCollapsed?: boolean;
  dataTestId?: string;
  children: ReactNode;
}

export const PermissionExpandableSection = ({
  title,
  subtitle,
  defaultCollapsed = false,
  dataTestId,
  children,
}: PermissionExpandableSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);

  return (
    <section
      className={styles.collapsibleSection}
      data-sl-state={isExpanded ? 'expanded' : 'collapsed'}
      data-testid={dataTestId}
    >
      <button
        type="button"
        className={styles.collapsibleHeader}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(expanded => !expanded)}
      >
        <div className={styles.collapsibleTitleWrap}>
          <span className={styles.collapsibleTitle}>
            <span className={styles.collapsibleChevron}>
              {isExpanded ? '▼' : '▶'}
            </span>
            <span>{title}</span>
          </span>
          {subtitle ? (
            <span className={styles.collapsibleSubtitle}>{subtitle}</span>
          ) : null}
        </div>
      </button>
      {isExpanded ? (
        <div className={styles.collapsibleContent}>{children}</div>
      ) : null}
    </section>
  );
};

export function usePendingPermissionAssignments() {
  const [pendingInvites, setPendingInvites] = useState<
    PendingPermissionInvite[]
  >([]);
  const [pendingGroupInvites, setPendingGroupInvites] = useState<
    PendingPermissionGroupInvite[]
  >([]);

  const resetPendingAssignments = useCallback(() => {
    setPendingInvites([]);
    setPendingGroupInvites([]);
  }, []);

  const addInvite = useCallback((invite: PendingPermissionInvite) => {
    setPendingInvites(prev => [...prev, invite]);
  }, []);

  const removeInvite = useCallback((userId: string) => {
    setPendingInvites(prev => prev.filter(invite => invite.userId !== userId));
  }, []);

  const changeInviteRole = useCallback((userId: string, role: string) => {
    setPendingInvites(prev =>
      prev.map(invite =>
        invite.userId === userId ? { ...invite, role } : invite
      )
    );
  }, []);

  const addGroupInvite = useCallback(
    (groupId: string, groupName: string, role: string) => {
      setPendingGroupInvites(prev => [...prev, { groupId, groupName, role }]);
    },
    []
  );

  const removeGroupInvite = useCallback((groupId: string) => {
    setPendingGroupInvites(prev =>
      prev.filter(group => group.groupId !== groupId)
    );
  }, []);

  const changeGroupInviteRole = useCallback((groupId: string, role: string) => {
    setPendingGroupInvites(prev =>
      prev.map(group =>
        group.groupId === groupId ? { ...group, role } : group
      )
    );
  }, []);

  return {
    addGroupInvite,
    addInvite,
    changeGroupInviteRole,
    changeInviteRole,
    pendingGroupInvites,
    pendingInvites,
    removeGroupInvite,
    removeInvite,
    resetPendingAssignments,
  };
}

/** Shared state and workspace-group loading for object create dialogs. */
export function useCreatePermissionAssignments(open: boolean) {
  const folderApiService = useService(FolderApiService);
  const workspaceId = useService(WorkspaceService).workspace.id;
  const { resetPendingAssignments, ...assignments } =
    usePendingPermissionAssignments();
  const [workspaceGroups, setWorkspaceGroups] = useState<GroupData[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  useEffect(() => {
    if (!open) return;
    const cachedGroups = folderApiService.getCachedGroups(workspaceId);
    setWorkspaceGroups(cachedGroups);
    setIsLoadingGroups(cachedGroups.length === 0);
    folderApiService
      .getGroups(workspaceId)
      .then(setWorkspaceGroups)
      .catch(error => {
        if (cachedGroups.length === 0) {
          console.error(error);
        }
      })
      .finally(() => setIsLoadingGroups(false));
  }, [folderApiService, open, workspaceId]);

  useEffect(() => {
    if (open) {
      resetPendingAssignments();
    }
  }, [open, resetPendingAssignments]);

  return {
    ...assignments,
    isLoadingGroups,
    workspaceGroups,
  };
}

interface CreatePermissionPeopleSectionProps {
  copy: PermissionInviteCopy;
  defaultRole: string;
  disabled: boolean;
  onAddInvite: (invite: PendingPermissionInvite) => void;
  onChangeInviteRole: (userId: string, role: string) => void;
  onRemoveInvite: (userId: string) => void;
  pendingInvites: PendingPermissionInvite[];
  roleOptions: PermissionRoleMenuOption<string>[];
}

export const CreatePermissionPeopleSection = ({
  copy,
  defaultRole,
  disabled,
  onAddInvite,
  onChangeInviteRole,
  onRemoveInvite,
  pendingInvites,
  roleOptions,
}: CreatePermissionPeopleSectionProps) => {
  const [searchText, setSearchText] = useState('');
  const pendingUserIds = useMemo(
    () => new Set(pendingInvites.map(i => i.userId)),
    [pendingInvites]
  );
  const visiblePendingInvites = useMemo(
    () =>
      pendingInvites.filter(invite =>
        matchesPermissionQuery(searchText, [invite.userName, invite.userEmail])
      ),
    [pendingInvites, searchText]
  );

  const handleSelectMember = useCallback(
    (member: Member, role: string | undefined) => {
      onAddInvite({
        userId: member.id,
        userName: member.name ?? '',
        userEmail: member.email ?? '',
        userAvatarUrl: member.avatarUrl ?? undefined,
        role: role ?? defaultRole,
      });
    },
    [defaultRole, onAddInvite]
  );

  return (
    <PermissionMemberInviteSearch
      title={copy.title}
      placeholder={copy.placeholder}
      noResultsText={copy.noResultsText}
      offlineMessage={copy.offlineMessage}
      roleOptions={roleOptions}
      defaultRole={defaultRole}
      existingUserIds={[...pendingUserIds]}
      disabled={disabled}
      searchText={searchText}
      onSearchTextChange={setSearchText}
      onSelectMember={handleSelectMember}
    >
      {visiblePendingInvites.length > 0 && (
        <div className={styles.pendingList}>
          {visiblePendingInvites.map(invite => (
            <div key={invite.userId} className={styles.pendingRow}>
              <PermissionMemberRow
                user={{
                  id: invite.userId,
                  name: invite.userName,
                  email: invite.userEmail,
                  avatarUrl: invite.userAvatarUrl,
                }}
                roleSelector={
                  <PermissionRoleMenu
                    role={invite.role}
                    options={roleOptions}
                    onSelect={role => onChangeInviteRole(invite.userId, role)}
                  />
                }
                onRemove={() => onRemoveInvite(invite.userId)}
              />
            </div>
          ))}
        </div>
      )}
    </PermissionMemberInviteSearch>
  );
};

interface CreatePermissionGroupsSectionProps {
  copy: PermissionInviteCopy;
  defaultRole: string;
  isLoadingGroups: boolean;
  offline: boolean;
  onAddGroup: (groupId: string, groupName: string, role: string) => void;
  onChangeGroupRole: (groupId: string, role: string) => void;
  onRemoveGroup: (groupId: string) => void;
  pendingGroupInvites: PendingPermissionGroupInvite[];
  roleOptions: PermissionRoleMenuOption<string>[];
  workspaceGroups: GroupData[];
}

export const CreatePermissionGroupsSection = ({
  copy,
  defaultRole,
  isLoadingGroups,
  offline,
  onAddGroup,
  onChangeGroupRole,
  onRemoveGroup,
  pendingGroupInvites,
  roleOptions,
  workspaceGroups,
}: CreatePermissionGroupsSectionProps) => {
  const [searchText, setSearchText] = useState('');
  const assignedGroupIds = useMemo(
    () => new Set(pendingGroupInvites.map(g => g.groupId)),
    [pendingGroupInvites]
  );
  const visiblePendingGroupInvites = useMemo(
    () =>
      pendingGroupInvites.filter(group =>
        matchesPermissionQuery(searchText, [group.groupName])
      ),
    [pendingGroupInvites, searchText]
  );

  const handleSelectGroup = useCallback(
    (group: GroupData, role: string | undefined) => {
      onAddGroup(group.id, group.name, role ?? defaultRole);
    },
    [defaultRole, onAddGroup]
  );

  if (isLoadingGroups) {
    return null;
  }

  return (
    <PermissionGroupInviteSearch
      title={copy.title}
      placeholder={copy.placeholder}
      noResultsText={copy.noResultsText}
      offlineMessage={copy.offlineMessage}
      roleOptions={roleOptions}
      defaultRole={defaultRole}
      groups={workspaceGroups}
      assignedGroupIds={assignedGroupIds}
      disabled={offline}
      showOfflineMessage={offline}
      searchText={searchText}
      onSearchTextChange={setSearchText}
      onSelectGroup={handleSelectGroup}
    >
      {visiblePendingGroupInvites.length > 0 && (
        <div className={styles.pendingList}>
          {visiblePendingGroupInvites.map(group => (
            <PermissionGroupRow
              key={group.groupId}
              groupName={group.groupName}
              roleSlot={
                <PermissionRoleMenu
                  role={group.role}
                  options={roleOptions}
                  onSelect={role => onChangeGroupRole(group.groupId, role)}
                />
              }
              onRemove={() => onRemoveGroup(group.groupId)}
            />
          ))}
        </div>
      )}
    </PermissionGroupInviteSearch>
  );
};
